import { type Ref, useEffect, useId, useRef, useState } from 'react';

import { type Campaign, type CardFieldsUpdate, type Content, type Idea, updateCardFields } from '../api.ts';
import { describeError } from '../data.tsx';
import { useToast } from '../toast.tsx';
import * as wording from '../wording.ts';

/** Design reference § Data model: `blocked_reason` is at most 200 characters. */
export const MAX_BLOCKED_REASON_LENGTH = 200;

/**
 * The fields every card carries (US-018): its campaign, its publish-by date and
 * why it is blocked, each saved on its own through
 * `PATCH /api/board/cards/:cardId` as soon as it changes (the select and the
 * date) or loses focus (the reason). Standalone: it takes the card's id and
 * values and the campaigns to offer, so the content page (US-031) and the
 * idea drawer (US-020) mount it as it is.
 */
export function CardFields({
  cardId,
  campaignId,
  dueOn,
  blockedReason,
  campaigns,
  onSaved,
  campaignRef,
}: {
  /** `content:<id>` or `idea:<id>`. */
  readonly cardId: string;
  readonly campaignId: string | null;
  readonly dueOn: string | null;
  readonly blockedReason: string | null;
  /** Every campaign; archived ones are only offered while the card is in one. */
  readonly campaigns: readonly Campaign[];
  /** The card as the server answered it, after a save. */
  readonly onSaved?: (answer: { content: Content } | { idea: Idea }) => void;
  /** For focusing the campaign field from outside (`e`, US-027). */
  readonly campaignRef?: Ref<HTMLSelectElement>;
}) {
  const toast = useToast();
  const id = useId();
  const [saving, setSaving] = useState(false);
  const [due, setDue] = useState(dueOn ?? '');
  const [reason, setReason] = useState(blockedReason ?? '');

  /**
   * What was last sent (or last read), so a change followed by its blur, or an
   * Enter followed by one, saves once rather than twice.
   */
  const sentDue = useRef(dueOn ?? '');
  const sentReason = useRef(blockedReason ?? '');

  // A save, a poll or another tab may change the card; follow it.
  useEffect(() => {
    sentDue.current = dueOn ?? '';
    setDue(dueOn ?? '');
  }, [dueOn]);
  useEffect(() => {
    sentReason.current = blockedReason ?? '';
    setReason(blockedReason ?? '');
  }, [blockedReason]);

  const save = async (update: CardFieldsUpdate, undo: () => void): Promise<void> => {
    setSaving(true);
    try {
      onSaved?.(await updateCardFields(cardId, update));
    } catch (cause: unknown) {
      undo();
      toast.error(describeError(cause));
    } finally {
      setSaving(false);
    }
  };

  const saveDue = (value: string): void => {
    if (value === sentDue.current) return;
    sentDue.current = value;
    void save({ dueOn: value === '' ? null : value }, () => {
      sentDue.current = dueOn ?? '';
      setDue(dueOn ?? '');
    });
  };

  const saveReason = (value: string): void => {
    const trimmed = value.trim();
    if (trimmed === sentReason.current) return;
    sentReason.current = trimmed;
    void save({ blockedReason: trimmed === '' ? null : trimmed }, () => {
      sentReason.current = blockedReason ?? '';
      setReason(blockedReason ?? '');
    });
  };

  const offered = campaigns.filter((campaign) => campaign.archivedAt === null || campaign.id === campaignId);

  return (
    <div className="card-fields">
      <div className="field">
        <label className="field__label" htmlFor={`${id}-campaign`}>
          {wording.CARD_FIELD_CAMPAIGN}
        </label>
        <select
          id={`${id}-campaign`}
          ref={campaignRef}
          className="field__input"
          value={campaignId ?? ''}
          disabled={saving}
          onChange={(event) => {
            const value = event.target.value;
            void save({ campaignId: value === '' ? null : value }, () => undefined);
          }}
        >
          <option value="">{wording.CARD_FIELD_NO_CAMPAIGN}</option>
          {offered.map((campaign) => (
            <option key={campaign.id} value={campaign.id}>
              {campaign.archivedAt === null
                ? campaign.name
                : `${campaign.name} ${wording.CARD_FIELD_ARCHIVED_CAMPAIGN}`}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label className="field__label" htmlFor={`${id}-due`}>
          {wording.CARD_FIELD_DUE}
        </label>
        <input
          id={`${id}-due`}
          className="field__input"
          type="date"
          value={due}
          onChange={(event) => {
            setDue(event.target.value);
            // A picked or cleared day is whole; a half-typed one waits for the blur.
            if (event.target.value === '' || /^\d{4}-\d{2}-\d{2}$/.test(event.target.value)) {
              saveDue(event.target.value);
            }
          }}
          onBlur={(event) => {
            saveDue(event.target.value);
          }}
        />
      </div>
      <div className="field">
        <label className="field__label" htmlFor={`${id}-blocked`}>
          {wording.CARD_FIELD_BLOCKED}
        </label>
        <div className="card-fields__row">
          <input
            id={`${id}-blocked`}
            className="field__input"
            type="text"
            maxLength={MAX_BLOCKED_REASON_LENGTH}
            placeholder={wording.CARD_FIELD_BLOCKED_PLACEHOLDER}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            onBlur={(event) => {
              saveReason(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') saveReason(event.currentTarget.value);
            }}
          />
          <button
            type="button"
            className="button"
            disabled={saving || (reason === '' && blockedReason === null)}
            onClick={() => {
              setReason('');
              saveReason('');
            }}
          >
            {wording.CARD_FIELD_CLEAR}
          </button>
        </div>
      </div>
    </div>
  );
}
